import type { HttpClient } from '@adapters/HttpClient';
import { ClientsApiAdapter, type ClientsFilters } from '@modules/Clients/List/adapters/ClientsApiAdapter';
import { useAuthStore } from '@shared/stores/authStore';

export type ClientReferenceFamily =
  | 'products'
  | 'orders'
  | 'preparations'
  | 'entries'
  | 'inventory'
  | 'receptions'
  | 'billing'
  | 'shops'
  | 'transport'
  | 'cross-docking'
  | 'affiliates'
  | 'preparation-calendar'
  | 'balances'
  | 'shipping-rules';

export interface ClientFilterReference {
  id: number;
  name: string;
}
interface ReferencePage {
  data: ClientFilterReference[];
  meta: { currentPage?: number; lastPage?: number; current_page?: number; last_page?: number; total: number };
}

/** Filter references never require opening the full Clients catalogue in v2. */
export class ClientFilterReferencesApiAdapter {
  private readonly legacy: ClientsApiAdapter;
  constructor(
    private http: HttpClient,
    private family: ClientReferenceFamily,
    private contextual?: boolean,
  ) {
    this.legacy = new ClientsApiAdapter(http);
  }

  get usesContextualReferences(): boolean {
    const state = useAuthStore.getState();
    return this.contextual ?? (state.authorization?.model_version === 2 || state.role?.model_version === 2);
  }

  async getClientsPaginated(
    filters: ClientsFilters = {},
  ): Promise<{ clients: ClientFilterReference[]; nextPage?: number; hasMore: boolean; total: number }> {
    if (!this.usesContextualReferences) return this.legacy.getClientsPaginated(filters);
    const response = await this.list({
      search: filters.search ?? filters.name ?? '',
      page: filters.page ?? 1,
      per_page: Math.min(filters.limit ?? 20, 100),
    });
    const current = response.meta.currentPage ?? response.meta.current_page ?? 1;
    const last = response.meta.lastPage ?? response.meta.last_page ?? 1;
    return {
      clients: response.data,
      nextPage: current < last ? current + 1 : undefined,
      hasMore: current < last,
      total: response.meta.total,
    };
  }

  async getClientById(id: number): Promise<ClientFilterReference> {
    if (!this.usesContextualReferences) return this.legacy.getClientById(id);
    const response = await this.list({ reference_id: id, page: 1, per_page: 1 });
    const client = response.data.find((row) => row.id === id);
    if (!client) throw new Error('Client reference not found');
    return client;
  }

  async getAll(): Promise<ClientFilterReference[]> {
    const clients: ClientFilterReference[] = [];
    let page: number | undefined = 1;
    while (page !== undefined) {
      const result = await this.getClientsPaginated({ page, limit: 100 });
      clients.push(...result.clients);
      page = result.nextPage;
    }
    return clients;
  }

  private list(params: Record<string, string | number>) {
    return this.http.get<ReferencePage>(`/${this.family}/lookups/clients`, params, {}, false);
  }
}
